import React, { useMemo } from "react";
import { Award, MapPinned } from "lucide-react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

const REGION_COLORS = {
  "Jakarta Barat": "#0B6B3A",
  "Jakarta Pusat": "#27AE60",
  "Jakarta Selatan": "#63C987",
  "Jakarta Timur": "#9DDEB4",
  "Jakarta Utara": "#F2C94C",
  "Kepulauan Seribu": "#D6B03F",
};

function PublicChartTooltip({ active, payload }) {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0].payload;
  return (
    <div
      className="min-w-[160px] rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg"
      data-testid="public-beneficiary-tooltip"
    >
      <p className="whitespace-nowrap text-xs font-bold text-[#1F2937]">{item.region}</p>
      <p className="mt-0.5 whitespace-nowrap text-xs text-[#0B6B3A]">
        {item.count} penerima manfaat
      </p>
    </div>
  );
}

export default function PublicBeneficiaryInfographic({ regions, totalBeneficiaries }) {
  const chartData = useMemo(() => regions.map((item) => ({
    ...item,
    value: totalBeneficiaries > 0 ? item.count : 1,
    color: REGION_COLORS[item.region] || "#B7E4C7",
  })), [regions, totalBeneficiaries]);

  return (
    <section
      className="grid gap-6 rounded-lg border border-gray-200 bg-white p-5 sm:p-6 lg:grid-cols-[0.9fr_1.1fr]"
      data-testid="public-beneficiary-infographic"
    >
      <div className="flex flex-col">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B6B3A]">
              Sebaran Wilayah
            </p>
            <h2 className="mt-2 font-display text-2xl font-extrabold text-[#1F2937]">
              Penerima Manfaat DKI Jakarta
            </h2>
          </div>
          <MapPinned className="h-6 w-6 shrink-0 text-[#27AE60]" aria-hidden="true" />
        </div>

        <div
          className="relative mx-auto mt-5 aspect-square w-full max-w-[330px]"
          data-testid="public-beneficiary-donut"
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Tooltip
                content={<PublicChartTooltip />}
                offset={18}
                allowEscapeViewBox={{ x: true, y: true }}
                wrapperStyle={{ zIndex: 30, pointerEvents: "none" }}
              />
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="region"
                cx="50%"
                cy="50%"
                innerRadius="58%"
                outerRadius="88%"
                paddingAngle={3}
                stroke="none"
                isAnimationActive={false}
              >
                {chartData.map((item) => (
                  <Cell key={item.region} fill={item.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 m-auto flex h-[42%] w-[42%]">
            <div className="flex h-full w-full flex-col items-center justify-center rounded-full border-8 border-white bg-[#F9FAFB] px-3 text-center shadow-sm">
              <Award className="h-5 w-5 text-[#27AE60]" aria-hidden="true" />
              <p className="mt-1 font-display text-3xl font-extrabold text-[#0B6B3A]">
                {totalBeneficiaries}
              </p>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wide text-[#6B7280]">
                Penerima
              </p>
            </div>
          </div>
        </div>
        <div className="mt-3 flex justify-start" data-testid="public-dashboard-data-year">
          <span className="rounded-full border border-[#B7E4C7] bg-[#F0FBF5] px-3 py-1.5 text-xs font-bold text-[#0B6B3A]">
            Data Tahun 2026
          </span>
        </div>
      </div>

      <div>
        <div className="grid content-center gap-2" data-testid="public-beneficiary-legend">
          {chartData.map((item, index) => {
            const percentage = totalBeneficiaries > 0
              ? Math.round((item.count / totalBeneficiaries) * 100)
              : 0;
            return (
              <div
                key={item.region}
                className="flex items-center justify-between gap-3 rounded-lg bg-[#F9FAFB] px-3 py-3"
                data-testid={`public-beneficiary-region-${index}`}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <span
                    className="h-3 w-3 shrink-0 rounded-full"
                    style={{ backgroundColor: item.color }}
                    aria-hidden="true"
                  />
                  <span className="truncate text-sm font-bold text-[#1F2937]">{item.region}</span>
                </span>
                <span className="shrink-0 text-right">
                  <strong className="block text-sm font-extrabold text-[#0B6B3A]">{item.count}</strong>
                  <span className="text-xs font-semibold text-[#6B7280]">{percentage}%</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}